import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import type { Category, CreateCategoryBody, UpdateCategoryBody } from '@/types/api';

export function useCategoriesQuery(): UseQueryResult<Category[], Error> {
  return useQuery<Category[], Error>({
    queryKey: queryKeys.categories(),
    queryFn: async ({ signal }) => {
      const response = await apiClient.listCategories(signal);
      return [...response.items].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
      );
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** slug -> display name, for rendering category badges without another round trip. */
export function useCategoryLabels(): Map<string, string> {
  const { data } = useCategoriesQuery();
  return useMemo(
    () => new Map((data ?? []).map((category) => [category.slug, category.name])),
    [data],
  );
}

function useCategoryInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.categories() });
    // A rename or merge changes what the catalogue shows, so lists have to be re-fetched too.
    void queryClient.invalidateQueries({ queryKey: queryKeys.links() });
  };
}

export function useCreateCategory() {
  const invalidate = useCategoryInvalidation();
  return useMutation<Category, Error, CreateCategoryBody>({
    mutationFn: (body) => apiClient.createCategory(body),
    onSuccess: invalidate,
  });
}

export function useUpdateCategory() {
  const invalidate = useCategoryInvalidation();
  return useMutation<Category, Error, { categoryId: string; body: UpdateCategoryBody }>({
    mutationFn: ({ categoryId, body }) => apiClient.updateCategory(categoryId, body),
    onSuccess: invalidate,
  });
}

export function useDeleteCategory() {
  const invalidate = useCategoryInvalidation();
  return useMutation<void, Error, string>({
    mutationFn: (categoryId) => apiClient.deleteCategory(categoryId),
    onSuccess: invalidate,
  });
}
